import Cookies from "js-cookie"
import React, { useEffect } from "react"
import Notifications from "react-notification-system-redux"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import ReactTooltip from "react-tooltip"
import { setLanguage } from "redux-i18n"

import { get as fetchUser } from "data/actions/user/auth"
import { getIsUserFresh } from "data/selectors/user"
import AlertBanner from "shared/components/AppAlertBanner"
import Throw from "shared/components/Throw"
import dayjs from "shared/services/date"
import { getUserLanguages } from "shared/services/languages"
import GlobalModal from "shared/views/GlobalModal"

import {
  AuthenticatedRoutes,
  HeadersRoutes,
  PlatformConnectRoutes,
  PublicRoutes,
} from "../routing"
import ErrorBoundary from "./ErrorBoundary"
import GetFeedbackSurveys from "./GetFeedbackSurveys/GetFeedbackSurveys"
import Meta from "./Meta"
import TokenConnectionHandler from "./NewAccount/Amazon/components/TokenConnectionHandler"
import Zendesk, { ZendeskAPI } from "./ZendeskConfig"

import "../shared/styles/Base.css"
import styles from "./App.module.css"

const zendeskKey = "742956f5-7583-4b3b-b72c-5dd18685b119"

export default function App() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const notifications = useSelector(state => state.notifications)
  const general = useSelector(state => state.general)
  const user = useSelector(state => state.user)
  const isUserFresh = useSelector(getIsUserFresh)

  useEffect(() => {
    const initialLang = getUserLanguages()

    dispatch(setLanguage(initialLang))
    dispatch(fetchUser())
    runModalObserver()
  }, [])

  useEffect(() => {
    const isLogged = !!user?.id
    const redirectTo = Cookies.get("redirectTo")

    if (redirectTo && redirectTo !== "redirected" && isLogged) {
      const expires = new Date(dayjs().add(30, "seconds").toString())
      Cookies.set("redirectTo", "redirected", { expires })
      navigate(redirectTo)
    }
  }, [user.id])
  const handleLoaded = () => {
    ZendeskAPI("messenger", "hide")
  }
  return (
    <ErrorBoundary>
      <Throw shouldThrow={general.errorLoadingInitialData} />
      <Meta />
      <AlertBanner />
      <TokenConnectionHandler />
      <HeadersRoutes stateReady={general.stateReady} userName={user.name} />
      <main className={styles.root}>
        {isUserFresh ? (
          <PlatformConnectRoutes isLoading={general.loading} />
        ) : (
          <>
            {general.stateReady ? (
              <AuthenticatedRoutes />
            ) : (
              <PublicRoutes isLoading={general.loading} />
            )}
          </>
        )}
      </main>
      <GlobalModal />
      <Notifications notifications={notifications} allowHTML />
      <ReactTooltip effect="solid" />
      <GetFeedbackSurveys />
      {general.overlay && (
        <div className={styles.overlay}>
          <div className={styles.overlayContent}>{general.overlayMessage}</div>
        </div>
      )}
      <Zendesk defer zendeskKey={zendeskKey} onLoaded={handleLoaded} />
    </ErrorBoundary>
  )
}

/**
 * fix for rect modal that does not change body class when modal is closed
 * checks all changes to the BODY child nodes
 * if a modal node is removed (closed), the modal class is also removed from the body classList
 * if this will be fixed by a react-modal update - runModalObserver code can be removed
 *
 * @returns undefined
 */

function runModalObserver() {
  const MUTATION_TYPE = "childList"
  const MODAL_NODE = "ReactModalPortal"
  const MODAL_CLASS = "ReactModal__Body--open"

  const targetNode = document.body
  const config = { attributes: false, childList: true, subtree: false }

  const callback = mutationList => {
    const addedNodes = mutationList.filter(
      mutation =>
        mutation.type === MUTATION_TYPE &&
        Array.from(mutation.addedNodes).some(node =>
          node?.classList?.contains(MODAL_NODE)
        )
    )

    if (addedNodes.length > 0) return

    const removedNodes = mutationList.filter(
      mutation =>
        mutation.type === MUTATION_TYPE &&
        Array.from(mutation.removedNodes).some(node =>
          node?.classList?.contains(MODAL_NODE)
        )
    )

    if (removedNodes.length === 0) return

    document.body.classList.remove(MODAL_CLASS)
  }

  const observer = new MutationObserver(callback)
  observer.observe(targetNode, config)
}
